import { createContext, useContext, useEffect, useState, useRef } from 'react'
import { supabase } from '../lib/supabase'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  const loadingProfileRef = useRef(null)

  useEffect(() => {
    let mounted = true

    const handleSession = async (session) => {
      if (!mounted) return

      console.log('Handling session:', session?.user?.email)
      setUser(session?.user ?? null)

      if (session?.user) {
        // Prevent duplicate requests for the same user
        if (loadingProfileRef.current === session.user.id) return

        loadingProfileRef.current = session.user.id

        try {
          await loadProfile(session.user.id)
        } finally {
          loadingProfileRef.current = null
        }
      } else {
        loadingProfileRef.current = null
        setProfile(null)
        if (mounted) setLoading(false)
      }
    }

    // Check active sessions immediately
    supabase.auth.getSession().then(({ data: { session } }) => {
      handleSession(session)
    }).catch(err => {
      console.error('GetSession error:', err)
      if (mounted) setLoading(false)
    })

    // Listen for changes on auth state (login, logout, etc.)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        handleSession(session)
      }
    )

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [])

  const loadProfile = async (userId) => {
    try {
      // Add a timeout to prevent hanging indefinitely
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Profile load timed out')), 2000)
      )

      const profilePromise = supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', userId)
        .single()

      const { data, error } = await Promise.race([profilePromise, timeoutPromise])

      if (error) {
        // If table doesn't exist or no profile found, just log and continue
        console.warn('Profile not found or table does not exist:', error.message)
        setProfile(null)
      } else {
        setProfile(data)
      }
    } catch (error) {
      console.error('Error loading profile:', error)
      setProfile(null)
    } finally {
      // Always set loading to false, even if profile fetch fails
      setLoading(false)
    }
  }

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    return { data, error }
  }

  const signInWithMicrosoft = async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'azure',
      options: {
        scopes: 'email profile',
        redirectTo: `${window.location.origin}/dashboard`,
      },
    })
    return { data, error }
  }

  const signUp = async (email, password, fullName) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
        },
      },
    })
    return { data, error }
  }

  const signOut = async () => {
    const { error } = await supabase.auth.signOut()
    return { error }
  }

  const value = {
    user,
    profile,
    loading,
    signIn,
    signInWithMicrosoft,
    signUp,
    signOut,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
